import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';
import type { Action, BoundActions, HookActionsFunction, HookFunction } from 'react-sweet-state';
import { createActionsHook, createHook, createStore } from 'react-sweet-state';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ACCESS_LINK } from '@confluence/named-routes/entry-points/routes.ts';

/**
 * Whether a viewer on the Access Link route has switched the page into edit mode.
 *
 * Edit mode is state and not a URL because on this route the URL carries the share token that
 * authorises the session — navigating away from it fails subsequent requests.
 *
 * A store rather than React context because global navigation renders above the Access Link page
 * and needs to read this; a provider mounted there would be invisible to it. `useLivePageMode` is
 * a store for the same reason.
 *
 * `isAvailable` is true only while that route is mounted, so elsewhere this is inert. Prefer
 * `useIsEditorOpen()` when the question is simply "is an editor open".
 */
type State = {
	/** True only while the Access Link route is mounted. */
	isAvailable: boolean;
	isEditMode: boolean;
};

const initialState: State = {
	isAvailable: false,
	isEditMode: false,
};

const setAvailable =
	(isAvailable: boolean): Action<State> =>
	({ setState }) => {
		// Leaving the route must also clear edit mode, or a later visit inherits it.
		setState(isAvailable ? { isAvailable } : { isAvailable: false, isEditMode: false });
	};

const enterEditMode =
	(): Action<State> =>
	({ setState }) => {
		setState({ isEditMode: true });
	};

const exitEditMode =
	(): Action<State> =>
	({ setState }) => {
		setState({ isEditMode: false });
	};

const actions = { setAvailable, enterEditMode, exitEditMode };

const AccessLinkEditModeStore = createStore<State, typeof actions>({
	initialState,
	actions,
	name: 'AccessLinkEditModeStore',
});

export type AccessLinkEditModeType = State & {
	enterEditMode: () => void;
	exitEditMode: () => void;
};

const useAccessLinkEditModeStore: HookFunction<
	State,
	BoundActions<State, typeof actions>,
	void
> = createHook(AccessLinkEditModeStore);

const isOnAccessLinkRouteArgs: Parameters<typeof useRouteName>[0] = {
	selector: (routeName) => {
		return ACCESS_LINK.name === routeName;
	},
};

/** Actions without a state subscription, so callers that only dispatch do not re-render. */
const useAccessLinkEditModeStoreActions: HookActionsFunction<BoundActions<State, typeof actions>> =
	createActionsHook(AccessLinkEditModeStore);

/** Reads Access Link edit mode from anywhere in the tree. No provider required. */
export const useAccessLinkEditMode = (): AccessLinkEditModeType => {
	const isAvailable = useRouteName(isOnAccessLinkRouteArgs) as boolean;
	const [{ isEditMode }, { enterEditMode: enter, exitEditMode: exit }] =
		useAccessLinkEditModeStore();

	return { isAvailable, isEditMode, enterEditMode: enter, exitEditMode: exit };
};

export const AccessLinkEditModeProvider: FC<{ children?: ReactNode }> = ({ children }) => {
	const { exitEditMode } = useAccessLinkEditModeStoreActions();

	useEffect(() => {
		return () => {
			void exitEditMode();
		};
	}, [exitEditMode]);

	return <>{children}</>;
};
